import { HDM_COLORS_KEY } from '@/data/constants/hdmDataKey';
import { NO_RANGE_INDICATOR, paintColorsPalette } from '@/data/constants/paintColorPalette';
import { useSettings } from '@/data/Settings';
import { LineRangeColor, PaintColor, PaintColorsState } from '@/data/types/ColorType';
import { laggyMiddleWare } from '@/data/utils/laggyMiddleWare';
import useSWR from 'swr';

// The Berel/BEHR paint catalogs used to be static `import`s, which bundled ~2.7MB of
// JSON into the client JS (parsed on the main thread wherever this hook is used —
// PDP, cart, order history, etc.). They now live in `public/` and are fetched on
// demand, once per session, with an immutable content-hash versioned URL.
const PAINT_BEREL_VERSION = process.env.PAINT_BEREL_VERSION ?? '';
const PAINT_HDM_CLIENT_VERSION = process.env.PAINT_HDM_CLIENT_VERSION ?? '';

const PAINT_BEREL_URL = `/pinturasberelhdm.json${
	PAINT_BEREL_VERSION ? `?v=${PAINT_BEREL_VERSION}` : ''
}`;
const PAINT_HDM_URL = `/pinturashdm-client.json${
	PAINT_HDM_CLIENT_VERSION ? `?v=${PAINT_HDM_CLIENT_VERSION}` : ''
}`;

/**
 * Load the pinturashdm/pinturasberelhdm JSON payloads, generate internal data structure.
 * @param the merged json data
 * @returns parsed json in new structure.
 */

const processPaintData = ({ BD: hdmPaints }: { BD: any[] }): PaintColorsState => {
	const allBrands: string[] = [];
	const allPaintLines: string[] = [];
	const allBaseColors: string[] = [];

	hdmPaints.forEach((paint) => {
		if (!allBrands.includes(paint?.marca)) {
			allBrands.push(paint?.marca);
		}
		if (!allPaintLines.includes(paint?.linea)) {
			allPaintLines.push(paint?.linea);
		}
		if (!allBaseColors.includes(paint?.base)) {
			allBaseColors.push(paint?.base);
		}
	});

	const allLineRangeColors: LineRangeColor[] = [];
	const allLineColors: any[] = [];

	allBrands.forEach((brand) => {
		allPaintLines.forEach((paintLine) => {
			const brandLine = hdmPaints.filter(
				(paint) => paint.marca === brand && paint.linea === paintLine
			);
			const allColorsByCode = new Map<string, PaintColor>();

			if (brandLine?.length === 0) return;

			paintColorsPalette.forEach((paletteGama) => {
				const colorsByCode = new Map<string, PaintColor>();
				const isPlaceholderRange = paletteGama.name === NO_RANGE_INDICATOR;
				brandLine.forEach((brandLine) => {
					let rangeColors = brandLine.hue.find((hue: any) => hue.gama === paletteGama.name);
					if (!rangeColors) return;
					rangeColors = JSON.parse(JSON.stringify(rangeColors));
					rangeColors.colores.forEach((color: any) => {
						color.base = brandLine?.base;
						color.range = rangeColors?.gama;
						color.code = color?.codigo;
						color.value = color?.valor;
						color.name = color?.nombre;
						// Same color code can appear in more than one base (e.g. Neutra/Deep)
						// for the same gama; keep only the first occurrence to avoid duplicates.
						const dedupKey = isPlaceholderRange ? `${color.base}|${color.code}` : color.code;
						if (!colorsByCode.has(dedupKey)) colorsByCode.set(dedupKey, color);
						if (!allColorsByCode.has(dedupKey)) allColorsByCode.set(dedupKey, color);
					});
				});

				const gama: LineRangeColor = {
					brand,
					line: paintLine,
					range: paletteGama.name,
					colors: [...colorsByCode.values()],
				};
				allLineRangeColors.push(gama);
			});

			allLineColors.push({
				line: paintLine,
				colors: [...allColorsByCode.values()],
			});
		});
	});

	return {
		lineRangeColors: allLineRangeColors,
		colorClassification: {
			allBrands,
			allPaintLines,
			allBaseColors,
			allLineColors,
		},
	};
};

// Combine the paint color databases from all brands (BEHR + BEREL). Each file
// keeps its own source/lifecycle; they are merged in-memory so processPaintData
const fetcher = async ([, berelEnabled]: [string, boolean]): Promise<PaintColorsState> => {
	const urls = berelEnabled ? [PAINT_HDM_URL, PAINT_BEREL_URL] : [PAINT_HDM_URL];
	const responses = await Promise.all(urls.map((url) => fetch(url)));
	const failed = responses.find((res) => !res.ok);
	if (failed) {
		throw new Error(`Failed to load paint color data (${failed.status})`);
	}
	const paintJsonData = await Promise.all(responses.map((res) => res.json()));
	const combinedPaintData = { BD: paintJsonData.flatMap(({ BD }) => BD) };
	return processPaintData(combinedPaintData);
};
/**
 * SWR hook to fetch paint colors data.
 */
export const usePaintColors = () => {
	const { settings } = useSettings();
	const berelEnabled = settings?.userData?.ENABLE_BEREL_PAINTS === 'true';
	const key: [string, boolean] = [HDM_COLORS_KEY, berelEnabled];
	const { data, error, isLoading, isValidating } = useSWR(key, fetcher, {
		use: [laggyMiddleWare],
		revalidateOnFocus: false,
		revalidateOnReconnect: false,
		revalidateIfStale: false,
		// Both files are content-hash versioned; an unchanged URL means unchanged
		dedupingInterval: 24 * 60 * 60 * 1000,
	});

	return {
		colorClassification: data?.colorClassification,
		lineRangeColors: data?.lineRangeColors,
		loading: !error && isLoading,
		isValidating,
		error,
	} as PaintColorsState;
};
